<div class="filter-bar">

  <!-- Row 1: search + filter pills -->
  <div class="filter-bar__top">
    @if (showSearch()) {
      <div class="search-wrap">
        <span class="material-symbols-outlined search-wrap__icon">search</span>
        <input
          class="search-wrap__input"
          type="text"
          [placeholder]="searchPlaceholder()"
          [value]="searchInput()"
          (input)="onSearch($any($event.target).value)"
        />
      </div>
    }

    @for (group of filterGroups(); track group.key) {
      @switch (group.type ?? 'select') {
        @case ('toggle') {
          <button
            class="pill pill--toggle"
            [class.pill--active]="isToggleOn(group.key)"
            type="button"
            role="switch"
            [attr.aria-checked]="isToggleOn(group.key)"
            (click)="toggleBoolean(group.key)">
            <span class="pill__label">{{ group.label }}</span>
            <span class="switch" [class.switch--on]="isToggleOn(group.key)">
              <span class="switch__knob"></span>
            </span>
          </button>
        }
        @case ('typeahead') {
          <div class="pill-wrap">
            <button
              class="pill"
              [class.pill--active]="isPillActive(group.key)"
              type="button"
              (click)="toggleTypeahead(group, $event)">
              <span class="pill__label">{{ group.label }}</span>
              @if (countActive(group.key) > 0) {
                <span class="pill__badge">{{ countActive(group.key) }}</span>
              }
              <span class="material-symbols-outlined pill__chevron">expand_more</span>
            </button>

            @if (isPillOpen(group.key)) {
              <div class="dropdown dropdown--typeahead"
                   [style.top.px]="dropdownPos()?.top"
                   [style.left.px]="dropdownPos()?.left">
                <div class="ta-search">
                  <span class="material-symbols-outlined ta-search__icon">search</span>
                  <input
                    #taInput
                    class="ta-search__input"
                    type="text"
                    [placeholder]="group.placeholder ?? ('tableFilterBar.searchPlaceholder' | translate)"
                    [value]="taQuery()"
                    (input)="onTypeaheadInput(group, $any($event.target).value)"
                    (keydown)="onTypeaheadKeydown(group, $event)"
                  />
                  @if (taLoading()) {
                    <span class="ta-search__spinner"></span>
                  } @else if (taQuery()) {
                    <button class="ta-search__clear" type="button" [attr.aria-label]="'tableFilterBar.clearSearch' | translate"
                            (click)="clearTypeaheadQuery(group); taInput.focus()">
                      <span class="material-symbols-outlined">close</span>
                    </button>
                  }
                </div>

                <div class="ta-results" [style.max-height.px]="dropdownPos()?.maxHeight">
                  @if (taQuery().trim().length < (group.minChars ?? 1)) {
                    <div class="ta-hint">
                      <span class="material-symbols-outlined">keyboard</span>
                      {{ 'tableFilterBar.minCharsHint' | translate: { count: group.minChars ?? 1 } }}
                    </div>
                  } @else if (taLoading() && taResults().length === 0) {
                    <div class="ta-hint">
                      <span class="material-symbols-outlined">progress_activity</span>
                      {{ 'tableFilterBar.searching' | translate }}
                    </div>
                  } @else if (taResults().length === 0) {
                    <div class="ta-hint">
                      <span class="material-symbols-outlined">search_off</span>
                      {{ 'tableFilterBar.noMatches' | translate: { query: taQuery().trim() } }}
                    </div>
                  } @else {
                    @for (item of taResults(); track item.id; let i = $index) {
                      <button
                        class="ta-option"
                        [class.ta-option--active]="i === taActive()"
                        type="button"
                        (mouseenter)="taActive.set(i)"
                        (click)="selectTypeahead(group, item)">
                        <span class="checkbox" [class.checkbox--checked]="isTypeaheadSelected(group.key, item.id)">
                          @if (isTypeaheadSelected(group.key, item.id)) {
                            <span class="material-symbols-outlined checkbox__check">check</span>
                          }
                        </span>
                        <span class="ta-option__text">
                          <span class="ta-option__label">
                            {{ highlightParts(item.label, taQuery()).pre
                            }}<mark>{{ highlightParts(item.label, taQuery()).mark }}</mark>{{
                              highlightParts(item.label, taQuery()).post }}
                          </span>
                          @if (item.sublabel) {
                            <span class="ta-option__sublabel">{{ item.sublabel }}</span>
                          }
                        </span>
                      </button>
                    }
                  }
                </div>
              </div>
            }
          </div>
        }
        @default {
          <div class="pill-wrap">
            <button
              class="pill"
              [class.pill--active]="isPillActive(group.key)"
              (click)="togglePill(group.key, $event)">
              <span class="pill__label">{{ group.label }}</span>
              @if (countActive(group.key) > 0) {
                <span class="pill__badge">{{ countActive(group.key) }}</span>
              }
              <span class="material-symbols-outlined pill__chevron">expand_more</span>
            </button>

            @if (isPillOpen(group.key)) {
              <div class="dropdown"
                   [style.top.px]="dropdownPos()?.top"
                   [style.left.px]="dropdownPos()?.left"
                   [style.max-height.px]="dropdownPos()?.maxHeight">
                @for (opt of group.options ?? []; track opt) {
                  <button
                    class="dropdown__option"
                    [class.dropdown__option--selected]="isOptionSelected(group.key, opt)"
                    (click)="toggleOption(group.key, opt)">
                    @if (isSingleSelect(group.key)) {
                      <span class="radio" [class.radio--checked]="isOptionSelected(group.key, opt)"></span>
                    } @else {
                      <span class="checkbox" [class.checkbox--checked]="isOptionSelected(group.key, opt)">
                        @if (isOptionSelected(group.key, opt)) {
                          <span class="material-symbols-outlined checkbox__check">check</span>
                        }
                      </span>
                    }
                    <span>{{ group.optionLabels?.[opt] ?? opt }}</span>
                  </button>
                }
              </div>
            }
          </div>
        }
      }
    }
    <div class="filter-bar__actions">
      <ng-content select="[actions]"></ng-content>
    </div>
  </div>

  <!-- Row 2: active filter chips (shown only when filters active) -->
  @if (hasActiveFilters()) {
    <div class="chips-row">
      <span class="chips-row__label">{{ 'tableFilterBar.filtersLabel' | translate }}</span>
      <div class="chips-row__list">
        @for (chip of activeChips(); track chip.key + ':' + chip.value) {
          <span class="chip">
            {{ chip.label ?? chip.value }}
            <button class="chip__remove" (click)="removeChip(chip.key, chip.value)" [attr.aria-label]="'tableFilterBar.removeChip' | translate: { value: chip.label ?? chip.value }">
              <span class="material-symbols-outlined">close</span>
            </button>
          </span>
        }
      </div>
      <xemex-text-button (click)="clearAll()">{{ 'tableFilterBar.clearFilters' | translate }}</xemex-text-button>
    </div>
  }

</div>
